import { formatDate } from '@angular/common'

import {
  AttachmentType,
  type FileChipView,
} from '@flowx/designer/modules/ai-builder/types/composer-file.types'
import {
  ConversationFile,
  ConversationFileRemoval,
  ConversationFileRemovalState,
  FileOpening,
  HarnessAttachmentState,
  type HarnessAttachmentStatus,
  type HarnessSentFile,
  type MakerFile,
  MakerFileState,
  SentFileMark,
  toAttachmentState,
} from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import { COMPOSER_FILE_TEXTS } from '@flowx/designer/modules/ai-builder/utils/composer-files.dictionary'
import {
  FILE_TYPE_GLYPH,
  FILE_TYPE_ICON,
} from '@flowx/designer/modules/ai-builder/utils/file-chip-view.util'
import {
  fileSize,
  fileTypeOf,
  mimeLabel,
} from '@flowx/designer/modules/ai-builder/utils/file-pick.util'
import {
  asArray,
  asBoolean,
  asOptionalNumber,
  asString,
  isRecord,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'

/** The file panel's copy, as the chat-files prototype words it. */
export const FILE_PANEL_TEXTS = {
  title: 'Files in this conversation',
  closeList: 'Close the files list',
  close: 'Close',
  footer: 'Files live with the conversation and are deleted with it.',
  remove: 'Remove from the conversation',
  retry: COMPOSER_FILE_TEXTS.retry,
  removed: 'Removed from the conversation',
  notRead: 'Not read',
} as const

/** When a row's file was sent: the time for today's, the day before it for an earlier one. */
const SENT_AT_FORMAT = { today: 'HH:mm', earlier: 'd MMM, HH:mm' } as const
const SENT_AT_LOCALE = 'en-US'

/** What a sent file says where its size would go, as the prototype words it; a ready file says
 * its size. */
export const SENT_FILE_TEXTS = {
  processing: 'Processing…',
  notSearchable: 'Not searchable',
  failed: 'Indexing failed',
  removed: 'Removed',
} as const

/** How each type opens, as the spec has it — but a Word document downloads by its name: no browser
 * tab can show one, so a tab would stay empty while the file saves under the blob's id. */
export const FILE_OPENING: Readonly<Record<AttachmentType, FileOpening>> = {
  [AttachmentType.Pdf]: FileOpening.Tab,
  [AttachmentType.Doc]: FileOpening.Download,
  [AttachmentType.Bpmn]: FileOpening.Tab,
  [AttachmentType.Image]: FileOpening.Preview,
  [AttachmentType.Other]: FileOpening.Download,
}

/** The mark a document-center state draws: a stored file that is gone reads as failed. */
const MAKER_FILE_MARK: Readonly<Record<MakerFileState, SentFileMark>> = {
  [MakerFileState.Processing]: SentFileMark.Processing,
  [MakerFileState.Ready]: SentFileMark.Ready,
  [MakerFileState.Failed]: SentFileMark.Failed,
  [MakerFileState.NotFound]: SentFileMark.Failed,
}

/** The mark a frame's state draws; a ready file that is not searchable is told apart after. */
const ATTACHMENT_STATE_MARK: Readonly<Record<HarnessAttachmentState, SentFileMark>> = {
  [HarnessAttachmentState.Uploading]: SentFileMark.Processing,
  [HarnessAttachmentState.Indexing]: SentFileMark.Processing,
  [HarnessAttachmentState.Ready]: SentFileMark.Ready,
  [HarnessAttachmentState.Failed]: SentFileMark.Failed,
}

/** The tag each mark carries where the size would go; why a file failed is its tooltip's. */
const MARK_NOTE: Readonly<Record<SentFileMark, string | null>> = {
  [SentFileMark.Attached]: null,
  [SentFileMark.Processing]: SENT_FILE_TEXTS.processing,
  [SentFileMark.Ready]: null,
  [SentFileMark.NotSearchable]: SENT_FILE_TEXTS.notSearchable,
  [SentFileMark.Failed]: SENT_FILE_TEXTS.failed,
  [SentFileMark.Removed]: SENT_FILE_TEXTS.removed,
}

/** The glyph a sent file's chip draws in each mark: null where the type's own glyph stands. */
const SENT_FILE_MARK_GLYPH: Readonly<Record<SentFileMark, string | null>> = {
  [SentFileMark.Attached]: null,
  [SentFileMark.Processing]: null,
  [SentFileMark.Ready]: null,
  [SentFileMark.NotSearchable]: null,
  [SentFileMark.Failed]: 'ph-warning-circle',
  [SentFileMark.Removed]: null,
}

const NOT_REMOVED: ConversationFileRemovalState = {
  status: ConversationFileRemoval.None,
  error: null,
}

/**
 * The conversation's files in the document center's order — newest first, as app-manager sorts
 * them — each with where its removal has got to. A state the client does not know reads as sent.
 */
export function conversationFiles(
  files: readonly MakerFile[],
  removals: Readonly<Record<string, ConversationFileRemovalState>>
): readonly ConversationFile[] {
  return files.map((file) => ({
    id: file.storeEntryUuid,
    name: file.name,
    size: file.size ?? null,
    mime: file.mimeType,
    type: fileTypeOf(file.mimeType),
    sentAt: Date.parse(file.createdDate),
    mark: MAKER_FILE_MARK[file.state] ?? SentFileMark.Attached,
    failureReason: file.failureReason ?? null,
    retryable: file.state === MakerFileState.Failed,
    removal: removals[file.storeEntryUuid] ?? NOT_REMOVED,
  }))
}

/** A row's second line: "{LABEL} · {size} · sent {HH:mm}", the day before the time for a file
 * not sent today, without the size while the list carries none. */
export function fileRowMeta(file: ConversationFile, now: number = Date.now()): string {
  const size = file.size === null ? '' : ` · ${fileSize(file.size)}`

  if (Number.isNaN(file.sentAt)) {
    return `${mimeLabel(file.mime)}${size}`
  }

  const sentToday = new Date(file.sentAt).toDateString() === new Date(now).toDateString()
  const sent = formatDate(
    file.sentAt,
    sentToday ? SENT_AT_FORMAT.today : SENT_AT_FORMAT.earlier,
    SENT_AT_LOCALE
  )

  return `${mimeLabel(file.mime)}${size} · sent ${sent}`
}

/** The line under a row: "Removed from the conversation" once removed, why a removal failed, else
 * the file's tag — a failed one as "Not read: {reason}", as the prototype has it — or none. */
export function fileRowState(file: ConversationFile): string | null {
  if (file.removal.status === ConversationFileRemoval.Removed) {
    return FILE_PANEL_TEXTS.removed
  }

  const notRead =
    file.mark === SentFileMark.Failed && file.failureReason
      ? `${FILE_PANEL_TEXTS.notRead}: ${file.failureReason}`
      : null

  return file.removal.error ?? notRead ?? MARK_NOTE[file.mark]
}

/** How a row is painted at each step of its removal: its name, and the state line under it; an
 * empty state leaves the line to the file's mark. */
const REMOVAL_TINT: Readonly<Record<ConversationFileRemoval, { name: string; state: string }>> = {
  [ConversationFileRemoval.None]: { name: 'text-builder-soft', state: '' },
  [ConversationFileRemoval.Removing]: { name: 'text-builder-muted', state: '' },
  [ConversationFileRemoval.Removed]: {
    name: 'text-builder-dim line-through',
    state: 'text-builder-dim',
  },
  [ConversationFileRemoval.Failed]: { name: 'text-builder-soft', state: 'text-builder-danger' },
}

/** The tint of a row's state line while nothing is being removed: a failure in the danger tint. */
const MARK_STATE_TINT: Readonly<Record<SentFileMark, string>> = {
  [SentFileMark.Attached]: 'text-builder-dim',
  [SentFileMark.Processing]: 'text-builder-dim',
  [SentFileMark.Ready]: 'text-builder-dim',
  [SentFileMark.NotSearchable]: 'text-builder-dim',
  [SentFileMark.Failed]: 'text-builder-danger',
  [SentFileMark.Removed]: 'text-builder-dim',
}

/** A panel row's tints: its name's, and its state line's. */
export function fileRowTint(file: ConversationFile): { name: string; state: string } {
  const removal = REMOVAL_TINT[file.removal.status]

  return { name: removal.name, state: removal.state || MARK_STATE_TINT[file.mark] }
}

/** Where the document center has the conversation's stored files: the ones the maker took out,
 * and the mark each listed one draws. */
export type SentFileStanding = {
  removedIds: ReadonlySet<string>
  listedMarks: ReadonlyMap<string, SentFileMark>
}

const NO_STANDING: SentFileStanding = { removedIds: new Set(), listedMarks: new Map() }

/** Which mark a sent file draws: removed once the maker took its stored entry out of the
 * conversation, else from its last `attachment` frame — but a stored file still processing, which
 * no frame reports on any more, as the document center lists it. */
export function sentFileMark(
  file: HarnessSentFile,
  { removedIds, listedMarks }: SentFileStanding = NO_STANDING
): SentFileMark {
  if (file.storeEntryUuid && removedIds.has(file.storeEntryUuid)) {
    return SentFileMark.Removed
  }

  if (!file.state) {
    return SentFileMark.Attached
  }

  const mark = ATTACHMENT_STATE_MARK[file.state]
  const listed = file.storeEntryUuid ? listedMarks.get(file.storeEntryUuid) : undefined

  if (mark === SentFileMark.Processing && listed) {
    return listed
  }

  return mark === SentFileMark.Ready && !file.searchable ? SentFileMark.NotSearchable : mark
}

/** What a sent file's chip says after its name: "Processing…", "Not searchable", "Indexing
 * failed", "Removed", or else its size — none for a file read back from history. */
export function sentFileMeta(
  file: HarnessSentFile,
  standing: SentFileStanding = NO_STANDING
): string {
  return MARK_NOTE[sentFileMark(file, standing)] ?? (file.size === null ? '' : fileSize(file.size))
}

/** The chip's tooltip: name, type, size where known, and how its reading went — a failure in the
 * harness's own words when it gave some. */
export function sentFileTooltip(
  file: HarnessSentFile,
  standing: SentFileStanding = NO_STANDING
): string {
  const size = file.size === null ? '' : ` · ${fileSize(file.size)}`
  const note = MARK_NOTE[sentFileMark(file, standing)]
  const reason = note && file.failureReason ? `: ${file.failureReason}` : ''

  return `${file.name} · ${mimeLabel(file.mime)}${size}${note ? ` · ${note}${reason}` : ''}`
}

/** A sent file as the chip under its message draws it: what it is, and how its reading went. A
 * removed one greys its type glyph with its text. */
export function sentChipView(
  file: HarnessSentFile,
  standing: SentFileStanding = NO_STANDING
): FileChipView {
  const mark = sentFileMark(file, standing)
  const type = fileTypeOf(file.mime)

  return {
    glyph:
      SENT_FILE_MARK_GLYPH[mark] ??
      (mark === SentFileMark.Removed ? FILE_TYPE_ICON[type] : FILE_TYPE_GLYPH[type]),
    loading: mark === SentFileMark.Processing,
    progress: null,
    name: file.name,
    tooltip: sentFileTooltip(file, standing),
    meta: sentFileMeta(file, standing),
    failed: mark === SentFileMark.Failed,
    // Its stored entry is what app-manager reads again and opens: named by the session read
    // after the turn, and gone once the maker takes the file out.
    retryable: mark === SentFileMark.Failed && !!file.storeEntryUuid,
    removed: mark === SentFileMark.Removed,
    openable: !!file.storeEntryUuid && mark !== SentFileMark.Removed,
  }
}

/** A sent message's files after one `attachment` frame: the file it names by `attachment_id`,
 * else the first by that name the harness has not named yet, in the order they were sent. */
export function withAttachmentStatus(
  files: readonly HarnessSentFile[],
  status: HarnessAttachmentStatus
): readonly HarnessSentFile[] {
  const byId = status.attachmentId
    ? files.findIndex((file) => file.attachmentId === status.attachmentId)
    : -1
  const target =
    byId >= 0
      ? byId
      : files.findIndex((file) => !file.attachmentId && file.name === status.fileName)

  return files.map((file, index) =>
    index === target
      ? {
          ...file,
          attachmentId: status.attachmentId || file.attachmentId,
          state: status.state,
          searchable: status.searchable,
          failureReason: status.failureReason,
        }
      : file
  )
}

/** The states a file is only in while its turn runs. */
const IN_FLIGHT_STATES: ReadonlySet<HarnessAttachmentState | null> = new Set([
  HarnessAttachmentState.Uploading,
  HarnessAttachmentState.Indexing,
])

/** The ending turn's message files once the turn is over — ended, stopped or cut: one the harness
 * stopped reporting on is no longer being read, so it shows as sent rather than processing
 * forever. A file with a stored entry is not the turn's — a turn's own files are named only by the
 * read after it — but one sent back for indexing, which no turn reports on: it keeps its state. */
export function withSettledFiles<T extends { type: string; files?: readonly HarnessSentFile[] }>(
  transcript: readonly T[]
): readonly T[] {
  const last = transcript.findLastIndex((entry) => entry.type === 'user')
  const isSettling = (file: HarnessSentFile): boolean =>
    IN_FLIGHT_STATES.has(file.state) && !file.storeEntryUuid

  return transcript.map((entry, index) =>
    index === last && entry.files?.some(isSettling)
      ? {
          ...entry,
          files: entry.files.map((file) => (isSettling(file) ? { ...file, state: null } : file)),
        }
      : entry
  )
}

/** Every message's copy of a file sent back for indexing, processing again under the store entry
 * the retry gave it. Nothing streams its outcome to the thread: the open panel lists the files
 * again until it has one, and its chip reads it from that list. */
export function withRetriedFile<T extends { type: string; files?: readonly HarnessSentFile[] }>(
  transcript: readonly T[],
  storeEntryUuid: string,
  retriedEntryUuid: string
): readonly T[] {
  return transcript.map((entry) =>
    entry.files?.some((file) => file.storeEntryUuid === storeEntryUuid)
      ? {
          ...entry,
          files: entry.files.map((file) =>
            file.storeEntryUuid === storeEntryUuid
              ? {
                  ...file,
                  storeEntryUuid: retriedEntryUuid,
                  state: HarnessAttachmentState.Indexing,
                  failureReason: null,
                }
              : file
          ),
        }
      : entry
  )
}

/** A user message's files as a history read carries them: `files: [{attachment_id,
 * store_entry_uuid, file_name, mime_type, kind, size, state, searchable, failure_reason?}]`. The
 * harness keeps no content hash for them. */
export function toHistoryFiles(value: unknown): readonly HarnessSentFile[] {
  return asArray(value)
    .filter(isRecord)
    .filter((file) => asString(file['file_name']))
    .map((file) => {
      const state = toAttachmentState(file['state'])

      return {
        name: asString(file['file_name']),
        size: asOptionalNumber(file['size']) ?? null,
        hash: null,
        mime: asString(file['mime_type']),
        tempFileId: null,
        attachmentId: asString(file['attachment_id']) || null,
        storeEntryUuid: asString(file['store_entry_uuid']) || null,
        state,
        searchable: asBoolean(file['searchable'], state === HarnessAttachmentState.Ready),
        failureReason: asString(file['failure_reason']) || null,
      }
    })
}

/**
 * Every message's files, each with the document-center entry it became, as the session read names
 * them. The `attachment` frames carry no entry, so a file sent from this tab cannot be opened until
 * this runs — and a read dropped for a quicker follow-up leaves its message to the next one.
 * Matched by `attachment_id`, never by name: two files may share one.
 */
export function withStoredEntries<T extends { type: string; files?: readonly HarnessSentFile[] }>(
  transcript: readonly T[],
  events: readonly { event: string; data: Record<string, unknown> }[]
): readonly T[] {
  const isUnnamed = (file: HarnessSentFile): boolean => !file.storeEntryUuid && !!file.attachmentId

  if (!transcript.some((entry) => entry.files?.some(isUnnamed))) {
    return transcript
  }

  const stored = new Map(
    events
      .filter((event) => event.event === 'user')
      .flatMap((event) => asArray(event.data['files']))
      .filter(isRecord)
      .map((file) => [asString(file['attachment_id']), asString(file['store_entry_uuid'])] as const)
      .filter(([attachmentId, storeEntryUuid]) => attachmentId && storeEntryUuid)
  )
  const named = (file: HarnessSentFile): HarnessSentFile =>
    isUnnamed(file) && stored.has(file.attachmentId ?? '')
      ? { ...file, storeEntryUuid: stored.get(file.attachmentId ?? '') ?? null }
      : file

  return transcript.map((entry) =>
    entry.files?.some((file) => named(file) !== file)
      ? { ...entry, files: entry.files.map(named) }
      : entry
  )
}
